/**
 * Utilitaire de formatage de dates - Format français jj/mm/aaaa
 */

/**
 * Formate une date au format jj/mm/aaaa
 * @param {string|Date} dateInput - Date à formater
 * @returns {string} Date formatée ou chaîne vide si invalide
 */
export const formatDate = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    
    // Si c'est déjà au format jj/mm/aaaa, on le retourne tel quel
    if (typeof dateInput === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)) {
      return dateInput;
    }
    
    // Si c'est au format ISO (YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss)
    if (typeof dateInput === 'string') {
      // Nettoyer la date
      const cleanDate = dateInput.split('T')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDate)) {
        const [year, month, day] = cleanDate.split('-');
        return `${day}/${month}/${year}`;
      }
      date = new Date(dateInput);
    } else {
      date = dateInput;
    }
    
    if (isNaN(date.getTime())) return dateInput;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateInput || '';
  }
};

/**
 * Formate une date avec l'heure au format jj/mm/aaaa HH:mm
 * @param {string|Date} dateInput - Date à formater
 * @returns {string} Date et heure formatées
 */
export const formatDateTime = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) return dateInput;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch (e) {
    return dateInput || '';
  }
};

/**
 * Formate une date pour affichage long (ex: "28 mars 2026")
 * @param {string|Date} dateInput - Date à formater
 * @returns {string} Date formatée en texte
 */
export const formatDateLong = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    
    if (typeof dateInput === 'string') {
      // Handle ISO format YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}/.test(dateInput)) {
        date = new Date(dateInput);
      } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)) {
        // Handle dd/mm/yyyy format
        const [day, month, year] = dateInput.split('/');
        date = new Date(year, month - 1, day);
      } else {
        date = new Date(dateInput);
      }
    } else {
      date = dateInput;
    }
    
    if (isNaN(date.getTime())) return dateInput;
    
    const months = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  } catch (e) {
    return dateInput || '';
  }
};

/**
 * Convertit jj/mm/aaaa en YYYY-MM-DD pour les inputs HTML
 * @param {string} dateStr - Date au format jj/mm/aaaa
 * @returns {string} Date au format YYYY-MM-DD
 */
export const toISODate = (dateStr) => {
  if (!dateStr) return '';
  
  // Si déjà au format ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Si au format jj/mm/aaaa
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  }
  
  return dateStr;
};

export default { formatDate, formatDateTime, formatDateLong, toISODate };
